import { Dropdown, Form, Input, Menu, Modal, notification, Row, Select, TableColumnsType } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineCloudDownload, AiOutlineCloudUpload, AiOutlineEdit } from 'react-icons/ai';
import { trackPromise, usePromiseTracker } from 'react-promise-tracker';
import { useUserContextHook } from '../../hooks/useUserContextHook';
import { ICreditNoteWaybills, INoteChildren, IUpdateCNDNRequest } from '../../model/CNDNModel';
import { useCNDNStore } from '../../stores/CNDNStore';
import { CNDN_DIRECTOR_POSITIONS, CNDN_FINANCE_POSITIONS, CNDN_MANIFESTER_POSITIONS, DD_MM_YYYY_H_MM, DEFAULT_CURRENT, DEFAULT_PAGE_SIZE, dropDownMapping, ERROR, FILE_SIZE_EXCEEDS, fileFormat, fileInclude, isCnMainPageAccess, isNotEmptyObject, TWO_HUNDRED_SUCCESS_CODE, TWO_MB_TO_BYTES, UserEnum } from '../../utils/app-common';
import { convertDateFromMillis } from '../../utils/luxon';
import { amendAmountValidation, requiredValidation } from '../../validation/validation';
import TableComponent from '../CommonComponent/TableComponent';
import { useOperationStore } from '../../stores/OperationStore';
import LoadingModalComponent from '../CommonComponent/LoadingModalComponent';
import { emptyUploadedFileValue } from '../../stores/common';
import { ICommonState, IFileUpload } from '../../model/CommonModel';
import { useCommonStore } from '../../stores/CommonStore';
import { FcCancel } from 'react-icons/fc';
import { SessionStorage } from '../../services/storage';


type Props = {
    open: boolean,
    onClose: (value: boolean) => void,
    selectedRowId: string | undefined,
    onUploadFilesHandle: (waybill: string | undefined, files: File) => void;
}

const CnDnFileComponent = ({ open, onClose, selectedRowId, onUploadFilesHandle }: Props) => {

    const { promiseInProgress: podFilesActionArea } = usePromiseTracker({ area: 'pod-files-action-area' });
    const uploadFileResponse = useOperationStore((state) => state.uploadFileResponse);

    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const [files] = (event.target as HTMLInputElement).files || [];
        if (files?.size > TWO_MB_TO_BYTES) {
            Modal.error({
                title: ERROR,
                closable: true,
                content: FILE_SIZE_EXCEEDS,
                onOk: () => Modal.destroyAll()
            });
            return
        }
        onUploadFilesHandle(selectedRowId, files);
    }, [
        onUploadFilesHandle
        , selectedRowId]);

    useEffect(() => {
        if (isNotEmptyObject(uploadFileResponse) && uploadFileResponse?.statusCode === TWO_HUNDRED_SUCCESS_CODE) {
            // onClose(false)
        }
    }, [onClose, uploadFileResponse])
    return (
        <Modal maskClosable={false} open={open} width={500} onOk={() => onClose(false)} onCancel={() => onClose(false)} title={'Upload Files'}>
            <LoadingModalComponent isLoading={podFilesActionArea}>
                <Row>
                    <input className='upload'
                        type='file'
                        onChange={onChange}
                        onClick={emptyUploadedFileValue}
                        placeholder='UploadFile' />
                </Row>
            </LoadingModalComponent>
        </Modal>
    )
}


const EditModalComponent = ({ open, onCancel, record }: { open: boolean, onCancel: (value: boolean) => void, record: ICreditNoteWaybills }) => {
    const [form] = Form.useForm();
    const { employeeDetail } = useUserContextHook() || {}
    const { countryId } = employeeDetail || {};
    const {
        id,
        subClientId,
        waybillNo,
        grossCharge,
        oracleInvoiceNumber,
        cnWaybillNo,
        childNode,
        cndRequestId
    } = record ?? {};

    const UpdateCreditDebitNoteAction = useCNDNStore(state => state?.UpdateCreditDebitNoteAction);
    const GetCNDNWaybillAction = useCNDNStore(state => state?.GetCNDNWaybillAction);
    
    const { promiseInProgress: updateCNDNProgress } = usePromiseTracker({ area: 'update-credit-debit-area' });
    
    const { vatTableList } = JSON.parse(SessionStorage.get('app-storage') ?? '{}');

    const vatTablesMemo = useMemo(() => dropDownMapping(vatTableList, 'id', 'vatName', 'vatPercentage', '%').filter(item => item?.countryId === countryId),
        [vatTableList, countryId]);


    const GetCnNoteReasonsAction = useCommonStore((state: ICommonState) => state.GetCnNoteReasonsAction);
    const cnNoteReasonsList = useCommonStore((state: ICommonState) => state?.cnNoteReasonsList);

    useEffect(() => {
        trackPromise(GetCnNoteReasonsAction(), 'cn-search-area');
    }, [GetCnNoteReasonsAction])

    const cnNoteReasonsListMemo = useMemo(() => dropDownMapping(cnNoteReasonsList, 'id', 'reason', 'vatPercentage', '%'), [cnNoteReasonsList]);

    const cnNoteReasonsMap = useMemo(() => {
        const obj = {};
        cnNoteReasonsListMemo?.forEach((item) => {
            obj[item?.id] = item?.value;
        });
        return obj;
    }, [cnNoteReasonsListMemo]);

    if (isNotEmptyObject(record)) {
        form.setFieldsValue({ ...record });
    }

    const onFinishHandler = (values: { amendCharge: number,  cnNoteReasonId: number, amendVatPercentageId: number, amendVatAmount: number, grossAmendCharge: number, remarks: string }) => {
        const { amendCharge, cnNoteReasonId, amendVatPercentageId, amendVatAmount, grossAmendCharge, remarks } = values;
       
               if (cnNoteReasonId === undefined && amendVatPercentageId === undefined) { // cn vat and dn vat cannot both be null
            notification.open({
                message: 'You must select credit note reason OR amend VAT percentage',
                style: {
                    color: 'rgba(0, 0, 0, 0.65)',
                    border: '1px solid #ffa39e',
                    backgroundColor: '#fff1f0'
                },
                icon: <FcCancel />
            });
            return;
        }
       
        const request: IUpdateCNDNRequest = {
            id: cndRequestId,
            subClientId: subClientId,
            cnRequestDetailsList: [{
                id,
                waybillNo,
                subClientId,
                grossCharge,
                amendCharge,
                amendVatPercentageId,
                amendVatAmount,
                grossAmendCharge,
                remarks,
                oracleInvoiceNumber,
                cnNoteReasonId
            }]
        };
        let getData: () => void = () => { onCancel(true); };
        if (CNDN_MANIFESTER_POSITIONS.includes(employeeDetail?.positionId!)) {
            getData = () => {
                trackPromise(GetCNDNWaybillAction({
                    pageNumber: DEFAULT_CURRENT, pageSize: DEFAULT_PAGE_SIZE, isDirectorConfirmedSet: false, isFinanceConfirmedSet: false
                }), 'get-cn-area');
                onCancel(true);
            };
        } else if (CNDN_DIRECTOR_POSITIONS.includes(employeeDetail?.positionId!)) {
            getData = () => {
                trackPromise(GetCNDNWaybillAction({
                    pageNumber: DEFAULT_CURRENT, pageSize: DEFAULT_PAGE_SIZE
                }), 'get-cn-area');
                onCancel(true);
            };
        }
        trackPromise(UpdateCreditDebitNoteAction({ id: cndRequestId, request, getData }), 'update-credit-debit-area');
    }

    const handleValuesChange = useCallback((_, values) => {
        let amendVatAmount = 0
        let grossAmendCharge = 0;
       if(values?.cnNoteReasonId){
         amendVatAmount = (values?.amendCharge === undefined ) || (values?.cnNoteReasonId === undefined) ? 0 : (Number(values?.amendCharge) * Number(cnNoteReasonsList?.find((el) => el?.id === values?.cnNoteReasonId)?.vatPercentage) / 100);
         grossAmendCharge = (values?.amendCharge === undefined ) || (values?.cnNoteReasonId === undefined) ? 0 : Number(values?.amendCharge) + (Number(values?.amendCharge) * Number(cnNoteReasonsList?.find((el) => el?.id === values?.cnNoteReasonId)?.vatPercentage) / 100);
        }
       else {
         amendVatAmount = (values?.amendCharge === undefined ) || (values?.amendVatPercentageId === undefined) ? 0 : (Number(values?.amendCharge) * Number(vatTableList?.find((el) => el?.id === values?.amendVatPercentageId)?.vatPercentage) / 100);
         grossAmendCharge = (values?.amendCharge === undefined ) || (values?.amendVatPercentageId === undefined) ? 0 : Number(values?.amendCharge) + (Number(values?.amendCharge) * Number(vatTableList?.find((el) => el?.id === values?.amendVatPercentageId)?.vatPercentage) / 100);
       }

        form.setFieldValue("amendVatAmount", amendVatAmount);
        form.setFieldValue("grossAmendCharge", grossAmendCharge);
    }, [form, vatTableList , cnNoteReasonsList ]);

    const columns = React.useMemo(() => [
        {
            title: 'Invoice Number',
            dataIndex: 'oracleInvoiceNumber',
            key: 'oracleInvoiceNumber',
            width: 40,
        }, 
        {
            title: 'Waybill No',
            dataIndex: 'waybillNo',
            key: 'waybillNo',
            width: 40,
        }, 
        {
            title: 'CN/DN Waybill No',
            dataIndex: 'cnWaybillNo',
            key: 'cnWaybillNo',
            width: 40,
        }, 
        {
            title: 'Created Date',
            dataIndex: 'createdDate',
            width: 40,
            key: 'createdDate',
            render: (createdDate: number) => createdDate ? convertDateFromMillis(createdDate, DD_MM_YYYY_H_MM) : ''
        }, 
        {
            title: 'Gross Amount',
            width: 20,
            dataIndex: 'grossCharge',
            key: 'grossCharge',
        },
        {
            title: 'Vat Amount',
            width: 20,
            dataIndex: 'vatAmount',
            key: 'vatAmount',
        },
        {
            title: 'Vat Percentage',
            width: 20,
            dataIndex: 'vatPercentage',
            key: 'vatPercentage',
        },
        {
            title: 'Int\' gross Charge',
            dataIndex: 'intGrossCharge',
            key: 'intGrossCharge',
            width: 20,
        },
        {
            title: 'Int\' Vat Amount',
            dataIndex: 'intVatAmount',
            key: 'intVatAmount',
            width: 20,
        },
        {
            title: 'Int\' Vat percentage',
            dataIndex: 'intVatPercentage',
            key: 'intVatPercentage',
            width: 20,
        },
        {
            title: 'Total Gross',
            width: 20,
            dataIndex: 'totalGross',
            key: 'totalGross',
        },
        {
            title: `Amendment Amount *`,
            width: 30,
            dataIndex: 'amendCharge',
            key: 'amendCharge',
            editable: true,
            render: (_, record) => (
                <Form.Item name='amendCharge' initialValue={record?.amendCharge} rules={[requiredValidation, amendAmountValidation]}>
                    <Input placeholder='Amendment Amount' maxLength={10} />
                </Form.Item>
            ),
        },
        {
            title: `Credit Note Reason & Amend VAT Percentage`,
            width: 60,
            dataIndex: 'cnNoteReasonId',
            key: 'cnNoteReasonId',
            editable: true,
            render: (_, record) => (
                <Form.Item name='cnNoteReasonId' initialValue={record?.cnNoteReasonId}
                //  rules={[requiredValidation]}
                >
                    <Select allowClear>
                        {
                            cnNoteReasonsListMemo?.map((item) => (
                                <React.Fragment key={item?.id}>
                                    <Select.Option key={item?.id} value={item?.id} >{item?.value}</Select.Option>
                                </React.Fragment>
                            ))
                        }
                    </Select>
                </Form.Item>
            ),
        },
        {
            title: `Amend VAT Percentage *`,
            width: 60,
            dataIndex: 'amendVatPercentageId',
            key: 'amendVatPercentageId',
            editable: true,
            render: (_, record) => (
                <Form.Item name='amendVatPercentageId' initialValue={record?.amendVatPercentageId} 
                // rules={[requiredValidation]}
                >
                    <Select allowClear>
                        {
                            vatTablesMemo?.map((item) => (
                                <React.Fragment key={item?.id}>
                                    <Select.Option key={item?.id} value={item?.id} >{item?.value}</Select.Option>
                                </React.Fragment>
                            ))
                        }
                    </Select>
                </Form.Item>
            ),
        },
        {
            title: `Amend VAT Amount`,
            width: 30,
            dataIndex: 'amendVatAmount',
            key: 'amendVatAmount',
            editable: true,
            render: (_, record) => (
                <Form.Item name='amendVatAmount' initialValue={record?.amendVatAmount}>
                    <Input disabled />
                </Form.Item>
            ),
        },
        {
            title: 'Gross Amendment Amount',
            width: 50,
            dataIndex: 'grossAmendCharge',
            key: 'grossAmendCharge',
            editable: true,
            render: (_, record) => (
                <Form.Item name='grossAmendCharge' initialValue={record?.grossAmendCharge}>
                    <Input disabled />
                </Form.Item>
            ),
        },
        {
            title: 'Remarks',
            width: 60,
            dataIndex: 'remarks',
            key: 'remarks',
            editable: true,
            render: (_, record) => (
                <Form.Item name='remarks' initialValue={record?.remarks}
                    rules={[
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                const trimmedValue = value?.trim();
                                let amendVatPercentage;
                                if(getFieldValue("cnNoteReasonId") !== undefined && getFieldValue("cnNoteReasonId") !== ''){
                                 amendVatPercentage = cnNoteReasonsList?.find((el) => el?.id === getFieldValue("cnNoteReasonId"))?.vatPercentage;
                                }
                                else{
                                 amendVatPercentage = vatTableList?.find((el) => el?.id === getFieldValue("amendVatPercentageId"))?.vatPercentage;
                                }                                
                                if (!trimmedValue && record?.vatPercentage !== amendVatPercentage) {
                                    return Promise.reject(new Error('Remarks is required for different percentages of VAT and Amend VAT'));
                                }
                                return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input placeholder='Remarks' maxLength={50} />
                </Form.Item>
            ),
        }
    ], [vatTableList , cnNoteReasonsList ]);

    return (
        <Modal title={`Edit - ${cnWaybillNo}`} open={open} onCancel={() => onCancel(false)} width={1500} onOk={form.submit}
            okText='Update & Close'
            confirmLoading={updateCNDNProgress}>
            <Form form={form} onFinish={onFinishHandler} autoComplete="off" onValuesChange={handleValuesChange}>
                <TableComponent isLoading={false}
                    rowKey='waybillNo'
                    scroll={{ x: 0, y: 0 }}
                    data={childNode}
                    columns={columns}
                    rowClassName="editable-row"
                    isRefresh={false}
                >
                </TableComponent>
            </Form>
        </Modal>
    )
}
const MenuListComponent = ({ record, fromSource }) => {
    const [isEdit, setIsEdit] = React.useState(false);
    const [selectedRowId, setSelectedRowId] = useState<string | undefined>();
    const [showFilesModal, setShowFilesModal] = useState(false);
    const UploadAdditionalFileAction = useOperationStore((state) => state.UploadAdditionalFileAction);
    const DownloadSingleFileAction = useCommonStore(state => state?.DownloadSingleFileAction)
    const GetCNDNWaybillAction = useCNDNStore(state => state?.GetCNDNWaybillAction);
    const GetConfirmedWaybillAction = useCNDNStore(state => state?.GetConfirmedWaybillAction);
    const uploadFileResponse = useOperationStore((state) => state.uploadFileResponse);
    const [status, setStatus] = useState<number | undefined>();

    const onEditHandler = () => {
        setIsEdit(true)
    };
    const { employeeDetail } = useUserContextHook() || {}

    // DIRECTOR AND FINANCE CANNOT EDIT EITHER IN TO BE APPROVED PAGE OR IN APPROVED PAGE
    const noAccess = CNDN_FINANCE_POSITIONS.includes(employeeDetail?.positionId!) ||
        (CNDN_DIRECTOR_POSITIONS.includes(employeeDetail?.positionId!)) ||
        isCnMainPageAccess();


    const onFileHandler = useCallback((row: ICreditNoteWaybills) => {
        setSelectedRowId(row?.waybillNo);
        setShowFilesModal(true);
    }, [])

    useEffect(() => {
        if (uploadFileResponse?.statusCode) {
            if (uploadFileResponse?.statusCode && uploadFileResponse?.statusCode === 200) {
                if (fromSource === 'request') {
                    trackPromise(GetCNDNWaybillAction({
                        pageNumber: 0, pageSize: 10,
                        isDirectorConfirmedSet: false, isFinanceConfirmedSet: false
                    }), 'get-cn-area');
                }
                else if (fromSource === 'approved') {
                    if (CNDN_DIRECTOR_POSITIONS.includes(employeeDetail?.positionId!)) {
                        trackPromise(GetConfirmedWaybillAction({
                            pageNumber: 0, pageSize: 10,
                            directorConfirmed: true, isDirectorConfirmedSet: true,
                            isFinanceConfirmedSet: false,
                        }), 'get-cn-area');
                    } else if (CNDN_FINANCE_POSITIONS.includes(employeeDetail?.positionId!)) {
                        trackPromise(GetConfirmedWaybillAction({
                            pageNumber: 0, pageSize: 10,
                            directorConfirmed: true, isDirectorConfirmedSet: true,
                            financeConfirmed: true, isFinanceConfirmedSet: true
                        }), 'get-cn-area');
                    }

                }
                else if (fromSource === 'director') {
                    trackPromise(GetCNDNWaybillAction({ pageNumber: 0, pageSize: 10, }), 'get-cn-area');
                }
                else if (fromSource === 'divisionHead') {
                    trackPromise(GetCNDNWaybillAction({ pageNumber: 0, pageSize: 10, directorConfirmed: true }), 'get-cn-area');
                }
                else if (fromSource === 'finance') {
                    trackPromise(GetCNDNWaybillAction({ pageNumber: 0, pageSize: 10, divisionHeasdConfirmed: true }), 'get-cn-area');
                }
                else if (fromSource === 'cfo') {
                    trackPromise(GetCNDNWaybillAction({ pageNumber: 0, pageSize: 10, financeConfirmed: true }), 'get-cn-area');
                }
                // Wait a moment to show the success message to the user
                setTimeout(() => {
                    window?.location?.reload(); // Refresh the page
                }, 2000);
            }
        }
    }, [uploadFileResponse])


    const UploadFilesHandler = useCallback((waybill: string | undefined, files: File) => {
        if (fileInclude(files?.type)) {
            const request: Partial<IFileUpload> = {
                wayBillNumber: waybill,
                category: "CN DN Waybill File",
                fileType: "CN DN Waybill File",
                file: files,
                fileFormat: files?.type,
                generic: String(false)
            }
            trackPromise(UploadAdditionalFileAction(waybill, request), 'pod-files-action-area');
        }
    }, [UploadAdditionalFileAction,
    ])

    const onDownloadFileHandler = (row) => {
        if (row?.fileId === undefined) {
            notification.open({
                message: 'File Not Found',
                style: {
                    color: 'rgba(0, 0, 0, 0.65)',
                    border: '1px solid #ffa39e',
                    backgroundColor: '#fff1f0'
                },
                icon: <FcCancel />
            });
            return;
        }
        trackPromise(DownloadSingleFileAction(row?.fileId, row?.fileType, fileFormat[row?.fileFormat]), 'download-file-area')
    };

    const items = [
        ...(noAccess ? [] : [{
            key: 1,
            icon: <AiOutlineEdit size={18} />,
            label: 'Edit',
            disabled: (Boolean(record?.approvedByDirectorId)), // MANIFESTOR CAN EDIT BEFORE APPROVED BY DIRECTOR
            onClick: onEditHandler,
        }]),
        {
            key: 2,
            label: 'Upload File',
            icon: <AiOutlineCloudUpload size={18} />,
            onClick: () => onFileHandler(record),
        },
        {
            key: 3,
            label: 'Download File',
            icon: <AiOutlineCloudDownload size={18} />,
            onClick: () => onDownloadFileHandler(record)
        },

    ];

    return (
        <>
            <Menu
                // hidden={noAccess}
                items={items}
            />
            {isEdit ? <EditModalComponent open={isEdit} onCancel={(value: boolean) => setIsEdit(value)} record={record} /> : null}
            {showFilesModal ?
                <CnDnFileComponent open={showFilesModal} onClose={setShowFilesModal} selectedRowId={selectedRowId}
                    onUploadFilesHandle={UploadFilesHandler}
                />
                : null}
        </>
    )
}

export const expandedRowRender = (record: ICreditNoteWaybills, index, indent, expanded: boolean, fromSource: string) => {
    const columns: TableColumnsType<INoteChildren> = [
        {
            title: 'waybillNo',
            dataIndex: 'waybillNo',
            key: 'waybillNo',
            width: 5,
        }, {
            title: 'CN/DN WaybillNo',
            dataIndex: 'cnWaybillNo',
            key: 'cnWaybillNo',
            width: 5,
        }, {
            title: 'cn Request',
            dataIndex: 'cnRequest',
            key: 'cnRequest',
            width: 5,
        }, {
            title: 'Invoice Number',
            dataIndex: 'oracleInvoiceNumber',
            key: 'oracleInvoiceNumber',
            width: 5,
        }, {
            title: 'gross Charge',
            dataIndex: 'grossCharge',
            key: 'grossCharge',
            width: 5,
        },
        {
            title: 'Vat Amount',
            dataIndex: 'vatAmount',
            key: 'vatAmount',
            width: 5,
        },
        {
            title: 'Vat percentage',
            dataIndex: 'vatPercentage',
            key: 'vatPercentage',
            width: 5,
        },
        {
            title: 'Internatioanl gross Charge',
            dataIndex: 'intGrossCharge',
            key: 'intGrossCharge',
            width: 5,
        },
        {
            title: 'Internatioanl Vat Amount',
            dataIndex: 'intVatAmount',
            key: 'intVatAmount',
            width: 5,
        },
        {
            title: 'Internatioanl Vat percentage',
            dataIndex: 'intVatPercentage',
            key: 'intVatPercentage',
            width: 5,
        },
        {
            title: 'total Gross',
            dataIndex: 'totalGross',
            key: 'totalGross',
            width: 5,
        },
        {
            title: 'amend Charge',
            dataIndex: 'amendCharge',
            key: 'amendCharge',
            width: 5,
        },
        {
            title: 'Cn note reason & Amend VAT Percentage',
            dataIndex: 'cnNoteReason',
            key: 'cnNoteReason',
            width: 5,
        },
        {
            title: 'Amend VAT Percentage',
            dataIndex: 'amendVatPercentage',
            key: 'amendVatPercentage',
            width: 5,
        },
        {
            title: 'Amend VAT Amount',
            dataIndex: 'amendVatAmount',
            key: 'amendVatAmount',
            width: 5,
        },
        {
            title: 'gross Amend Charge',
            dataIndex: 'grossAmendCharge',
            key: 'grossAmendCharge',
            width: 5,
        }
        , {
            title: 'remarks',
            dataIndex: 'remarks',
            key: 'remarks',
            width: 5,
        }, {
            title: 'created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 5,
        }, {
            title: 'created Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 5,
            render: (_createdDate: number) => convertDateFromMillis(_createdDate, DD_MM_YYYY_H_MM),
        },
        {
            title: 'Is Pushed To Oracle',
            dataIndex: 'isPushedToOracle',
            key: 'isPushedToOracle',
            width: 5,
            render: (isPushedToOracle: boolean) => isPushedToOracle ? 'Yes' : 'No',
        },

        {
            title: 'Action',
            dataIndex: '',
            key: 'print',
            width: 5,
            render: (_, data: INoteChildren) => {
                return (
                    <Dropdown.Button
                        overlay={<MenuListComponent record={{ ...record, ...data, cndRequestId: record?.id }} fromSource={fromSource} />}>
                    </Dropdown.Button>
                )
            }
        }
    ];
    if (expanded) {
        return (
            <TableComponent
                rowSelected={false}
                showRowSelection={false}
                columns={columns}
                indentSize={indent}
                data={record?.childNode}
                hideOnSinglePage
                isLoading={false}
                scroll={{ x: 0, y: 0 }}
                rowKey='id'
                isRefresh={false} />
        )
    }
};
